/**
 * VerifyCode (Authentication) — 2FA code entry, rebuilt on the same
 * composition pattern as its mid-flow sibling PasswordRecovery (NOT the
 * logo-led SignIn/SignUp entry pages).
 *
 * The backend 2FA is TOTP (authenticator app): the code is generated on the
 * user's device — nothing is sent, and there is no resend (the schema exposes
 * only `verifyCode(code)` + `enable2fa(code, secret)`). So this screen has no
 * "code sent to…" line, no channel/target, and no resend affordance — just the
 * OTP field and its states.
 *
 * Layout tree:
 *   AuthShell
 *     CenteredCard (cardAs=form, radius=xl, paddingX=m, paddingTop=m,
 *                   paddingBottom=xl, gap=xl — no Glow: a 2FA step is not a
 *                   primary conversion screen)
 *       Row (Back button, arrow_back, left-aligned)
 *       Stack gap=m, alignX=center  (TitleBlock)
 *         Row (security icon + heading line)
 *         Description ("Введите код из приложения-аутентификатора")
 *       Stack gap=m, alignX=center  (FormBlock)
 *         CodeBlock (grid, fixed height control.height.xl=96px, top-aligned)
 *           FieldSlot → Input (segmented, 6-digit OTP) — auto-submits on the
 *                       6th digit; hidden (width kept) while verifying
 *           LoaderButton (mid-request only: primary spinner "Проверка")
 *
 * Presentational: the container owns the verify mutation and navigation
 * (`onSubmit`, `onBackClick`, `onLogoClick`). There is no confirm button —
 * `onSubmit(code)` fires automatically the moment the field holds 6 digits
 * (paste / autofill included). While the request is in flight the field is
 * covered by a primary spinner button; on failure the container feeds `error`
 * back and the filled field returns for correction.
 *
 * @format
 */

import {useEffect, useRef, useState} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';
import {useTranslation} from 'react-i18next';

import {Input} from 'ui/components/atoms/Input';
import {Button} from 'ui/components/atoms/Button';
import {Icon} from 'ui/components/atoms/Icon';
import {Stack} from 'ui/components/layout/Stack';
import {Row} from 'ui/components/layout/Row';
import {CenteredCard} from 'ui/components/composition/CenteredCard';
import {text} from 'ui/theme/typography';
import sizingTokens from 'ui/tokens/semantic/sizing.json';
import {v} from 'ui/tokens/util';

import {AuthShell} from '../_shell';

const CODE_LENGTH = 6;

export interface VerifyCodeProps {
  /** Preseed the code field. */
  initialCode?: string;
  /** Inline error overlay (e.g. wrong code). */
  error?: string;
  /** Mid-request override. */
  submitState?: 'idle' | 'submitting';
  /** Fires with the entered code when the field is complete. */
  onSubmit: (code: string) => void;
  /** Back button → navigate(-1) / sign-in. */
  onBackClick?: () => void;
  /** Logo click → external domain. */
  onLogoClick?: () => void;
}

// Heading row uses the heading.l role with an inline icon — one styled span
// binds the typography token without the chrome the Row primitive imposes.
// Mirrors PasswordRecovery's HeadingLine.
const HeadingLine = styled(Box)(({theme}) => ({
  ...text('heading.l'),
  color: theme.palette.surface.onSurface,
}));

const Description = styled('p')(({theme}) => ({
  ...text('body.l'),
  color: theme.palette.surface.onSurface,
  textAlign: 'center',
  margin: 0,
}));

// A fixed-height slot (control.height.xl = 96px) that holds the code field or,
// mid-request, the loader button — both stacked in one grid cell, top-aligned.
// The static height + the always-mounted field (which sets the width) keep the
// swap from shifting anything, and the reserved space below the top-aligned
// field absorbs the inline error. The width comes from the field even while
// it's visually hidden, so the loader lands on the exact same footprint.
const CodeBlock = styled(Box)({
  display: 'grid',
  gridTemplateAreas: '"field"',
  placeItems: 'start center',
  height: v(sizingTokens.control.height.xl),
  width: 'fit-content',
});

// Wraps the input in the shared grid cell; hidden (but still reserving width)
// while the request is in flight so the loader button reads as its replacement.
const FieldSlot = styled(Box, {
  shouldForwardProp: prop => prop !== '$hidden',
})<{$hidden: boolean}>(({$hidden}) => ({
  gridArea: 'field',
  visibility: $hidden ? 'hidden' : 'visible',
}));

// Mid-request stand-in: a primary spinner button in the same grid cell, on top
// of the (hidden) field. `fullWidth` stretches it to the field's width.
const LoaderButton = styled(Button)({
  gridArea: 'field',
});

export function VerifyCode({
  initialCode,
  error,
  submitState = 'idle',
  onSubmit,
  onBackClick,
  onLogoClick,
}: VerifyCodeProps) {
  const {t} = useTranslation();
  const [code, setCode] = useState(initialCode ?? '');

  const isSubmitting = submitState === 'submitting';

  // Auto-submit: verification fires the moment the field is complete — no
  // confirm button. `submittedCodeRef` holds the last code we sent so a server
  // error (which leaves all 6 digits in place, per the "keep the wrong code"
  // UX) can't re-fire in a loop. It resets whenever the field drops below full
  // length, so re-entering — even the *same* code after an error — submits
  // again. Seeded with a preseeded full `initialCode` so Storybook / Jobs
  // frames that render a filled field don't auto-fire on mount.
  const submittedCodeRef = useRef(
    initialCode && initialCode.length === CODE_LENGTH ? initialCode : '',
  );
  useEffect(() => {
    if (code.length < CODE_LENGTH) {
      submittedCodeRef.current = '';
      return;
    }
    if (!isSubmitting && submittedCodeRef.current !== code) {
      submittedCodeRef.current = code;
      onSubmit(code);
    }
  }, [code, isSubmitting, onSubmit]);

  return (
    <AuthShell onLogoClick={onLogoClick}>
      <CenteredCard
        maxWidthPx={420}
        radius='xl'
        paddingX='m'
        paddingTop='m'
        paddingBottom='xl'
        gap='xl'
        cardAs='form'
        cardProps={{onSubmit: e => e.preventDefault()}}
      >
        <Row justify='start'>
          <Button
            type='button'
            variant='ghost'
            color='neutral'
            size='m'
            leftIcon='arrow_back'
            onClick={onBackClick}
          >
            {t('Назад', 'Назад')}
          </Button>
        </Row>

        <Stack gap='m' alignX='center'>
          <Row gap='s' alignY='center'>
            <Icon name='security' size='l' color='on-surface' />
            <HeadingLine>{t('Подтверждение аккаунта', 'Подтверждение аккаунта')}</HeadingLine>
          </Row>
          <Description>
            {t(
              'Введите код из приложения-аутентификатора',
              'Введите код из приложения‑аутентификатора',
            )}
          </Description>
        </Stack>

        <Stack gap='m' alignX='center'>
          <CodeBlock>
            <FieldSlot $hidden={isSubmitting}>
              <Input
                mode='segmented'
                size='m'
                length={CODE_LENGTH}
                value={code}
                disabled={isSubmitting}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))}
                errorMessage={error}
              />
            </FieldSlot>

            {isSubmitting && (
              <LoaderButton
                type='button'
                variant='solid'
                color='primary'
                size='m'
                fullWidth
                loading='left'
              >
                {t('Проверка', 'Проверка')}
              </LoaderButton>
            )}
          </CodeBlock>
        </Stack>
      </CenteredCard>
    </AuthShell>
  );
}
